import GoogleTagLoader from "@/scripts/GoogleTagLoader";
import ToltLoader from "@/scripts/ToltLoader";
import AhrefLoader from "@/scripts/AhrefLoader";
import PosthogLoader from "@/scripts/PosthogLoader";
import ReduxLoader from "@/scripts/ReduxLoader";
import "@/styles/globals.css";
import type { AppProps } from "next/app";
import Navbar from "@/components/shared/Navbar";
import { ModalProvider } from "@/lib/contexts/ModalContext";
import ModalManager from "@/components/Modals/ModalManager";
import { initializeUtm } from "@/lib/utm";
import { useEffect } from "react";
import Head from "next/head";
import { Inter } from "next/font/google";
import { useRouter } from "next/router";
import { DefaultSeo } from "next-seo";

import * as amplitude from "@amplitude/analytics-browser";

const inter = Inter({
  subsets: ["latin"],
  display: "swap",
  weight: ["300", "400", "500", "600", "700"],
  variable: "--font-inter",
});

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  useEffect(() => {
    amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY!, { autocapture: false });
    initializeUtm();
  }, []);

  /**
   * Prevents janky scroll behavior when navigating back from blog posts
   * (disables browser scroll restoration and always scrolls to top)
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.scrollRestoration = "manual";
    }

    const handleRouteChange = () => {
      window.scrollTo(0, 0);
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, minimal-ui, maximum-scale=1"
        />
        <meta property="og:logo" content="https://crayo.ai/logo.png" />
      </Head>
      <DefaultSeo
        title="Crayo - The AI Video Editor"
        description="Generate viral shorts in seconds. Crayo is the all-in-one tool for creating AI voiceovers, engaging subtitles, optimized gameplay, and more."
        openGraph={{
          type: "website",
          locale: "en_US",
          url: "https://crayo.ai",
          site_name: "Crayo AI",
          title: "Crayo - The AI Video Editor",
          description:
            "Generate viral shorts in seconds. Crayo is the all-in-one tool for creating AI voiceovers, engaging subtitles, optimized gameplay, and more.",
          images: [
            {
              url: "https://crayo.ai/og-image.png",
              width: 1200,
              height: 630,
              alt: "Crayo - The AI Video Editor",
            },
          ],
        }}
        twitter={{
          handle: "@crayoai",
          site: "@crayoai",
          cardType: "summary_large_image",
        }}
      />
      <div className={`${inter.variable} min-h-[100dvh] font-sans`}>
        <ModalProvider>
          <ToltLoader />
          <ReduxLoader />
          <GoogleTagLoader />
          <PosthogLoader />
          <AhrefLoader />
          <Navbar />
          <Component {...pageProps} />
          <ModalManager />
        </ModalProvider>
      </div>
    </>
  );
}
